import {
  Box,
  Button,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  Tab,
  TablePagination,
  Tabs,
  TextField,
  useTheme,
} from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import useCustomStyles from '../../hooks/CustomStylesHook'
import Colors from '../../utils/colors.json'
import UserInfoCard, { User } from './UserInfoCard'
import AddIcon from '@mui/icons-material/Add'
import UserForm from './UserForm'
import {
  NOTIFICATION_TYPES,
  USER_DIALOG_MODES,
  USER_TABS,
} from '../../constants/constants'
import {
  createUser,
  getAllRoles,
  getUsers,
  updateUser,
} from '../../services/UserService'
import { useAppDispatch } from '../../redux/Hook'
import SearchIcon from '@mui/icons-material/Search'
import Loader from '../../components/Loader'

export interface UserRole {
  id: string
  name: string
  description: string
  composite: boolean
  clientRole: boolean
  containerId: string
}

const styles = (theme: any) => ({
  tabsContainer: {
    height: '5%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  tabSelected: {
    background: `${theme.palette.primary.main} !important`,
    borderRadius: '20px !important',
    transition: 'background-color 0.3s',
    color: '#FFFFFF !important',
    fontSize: '14px !important',
    minHeight: 'unset',
    textTransform: 'capitalize',
    // padding: '8px 8px',
  },
  tabUnSelected: {
    color: '#6C6C6C !important',
    fontSize: '14px !important',
    minHeight: 'unset',
    textTransform: 'capitalize',
    // padding: '8px 8px',
  },
  tabContentContainer: {
    flex: 1,
    overflowY: 'auto',
  },
  circleDot: {
    width: '6px',
    height: '6px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '50%',
    display: 'inline-block',
  },
  tabActiveCount: {
    color: Colors.TEXT_COLOR_TAB_ACTIVE,
  },
  tabInactiveCount: {
    color: Colors.TEXT_COLOR_TAB_INACTIVE,
  },
  createBtn: {
    background: theme.customColors.button,
    borderRadius: '18px',
    textTransform: 'capitalize',
  },
  cardPagination: {
    '& .MuiTablePagination-toolbar': {
      minHeight: 0,
      padding: '0 16px',
    },
  },
  searchField: {
    fontSize: '12px !important',
    borderRadius: 20,
    height: 30,
  },
})

const Users = () => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)

  const debounceRef = useRef<any>(null)
  const [value, setValue] = React.useState(0)
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(10)
  const [totalCount, setTotalCount] = React.useState(0)
  const [roles, setRoles] = React.useState<UserRole[]>([])
  const [searchTerm, setSearchTerm] = React.useState('')
  const [isLoading, setIsLoading] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  // eslint-disable-next-line
  const [users, setUsers] = useState<User[]>([])
  const [openDialog, setOpenDialog] = useState(false)
  const [dialogMode, setDialogMode] = useState('')
  const [selectedUser, setSelectedUser] = useState<User>({
    createdBy: '',
    createdOn: '',
    emailId: '',
    firstName: '',
    gender: '',
    keycloakUserId: '',
    lastName: '',
    middleName: '',
    phoneNumber: '',
    profile: null,
    role: '',
    status: '',
    updatedBy: null,
    updatedOn: null,
    userId: '',
    userName: '',
  })
  const [tabs, setTabs] = React.useState([
    { label: 'All', count: 0, color: '' },
    { label: 'Active', count: 0, color: Colors.TEXT_COLOR_TAB_ACTIVE },
    { label: 'Inactive', count: 0, color: Colors.TEXT_COLOR_TAB_INACTIVE },
  ])

  const handleCloseDialog = () => {
    setOpenDialog(false)
  }

  const handleCreate = (mode: string) => {
    setSelectedUser({
      createdBy: '',
      createdOn: '',
      emailId: '',
      firstName: '',
      gender: '',
      keycloakUserId: '',
      lastName: '',
      middleName: '',
      phoneNumber: '',
      profile: null,
      role: '',
      status: '',
      updatedBy: null,
      updatedOn: null,
      userId: '',
      userName: '',
    })
    handleDialogOpen(mode)
  }

  const handleDialogOpen = (mode: string) => {
    setDialogMode(mode)
    setOpenDialog(true)
  }

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
    setPage(0)
    setRowsPerPage(10)
  }

  const handleSubmit = (userData: User) => {
    setIsSubmitting(true)
    if (userData?.userId && dialogMode === USER_DIALOG_MODES?.UPDATE) {
      handleUpdateUser(userData?.userId, userData)
    } else {
      handleCreateUser(userData)
    }
  }

  const handleCreateUser = async (userData: User) => {
    const payload = {
      emailId: userData?.emailId,
      firstName: userData?.firstName,
      gender: userData?.gender,
      lastName: userData?.lastName,
      middleName: userData?.middleName,
      phoneNumber: userData?.phoneNumber,
      profile: userData?.profile,
      role: userData?.role,
      status: userData?.status,
      userName: userData?.userName,
    }
    const response = await createUser(payload)
    if (response?.success) {
      setIsSubmitting(false)
      handleCloseDialog()
      dispatch({
        type: 'SEND_ALERT',
        data: {
          enable: true,
          type: NOTIFICATION_TYPES.SUCCESS,
          message: response?.message,
          duration: 5000,
        },
      })
      getAllUsersApi()
    } else {
      setIsSubmitting(false)
      dispatch({
        type: 'SEND_ALERT',
        data: {
          enable: true,
          type: NOTIFICATION_TYPES.ERROR,
          message: response?.message,
          duration: 5000,
        },
      })
    }
  }

  const handleUpdateUser = async (userId: string | number, userData: User) => {
    const payload = {
      emailId: userData?.emailId,
      firstName: userData?.firstName,
      gender: userData?.gender,
      lastName: userData?.lastName,
      middleName: userData?.middleName,
      phoneNumber: userData?.phoneNumber,
      profile: userData?.profile,
      role: userData?.role,
      status: userData?.status,
      userName: userData?.userName,
    }
    const response = await updateUser(userId, payload)
    if (response?.success) {
      setIsSubmitting(false)
      handleCloseDialog()
      dispatch({
        type: 'SEND_ALERT',
        data: {
          enable: true,
          type: NOTIFICATION_TYPES.SUCCESS,
          message: response?.message,
          duration: 5000,
        },
      })
      getAllUsersApi()
    } else {
      setIsSubmitting(false)
      dispatch({
        type: 'SEND_ALERT',
        data: {
          enable: true,
          type: NOTIFICATION_TYPES.ERROR,
          message: response?.message,
          duration: 5000,
        },
      })
    }
  }

  const getAllUsersApi = useCallback(async () => {
    setIsLoading(true)
    const getTab = (value: number) => {
      switch (value) {
        case 0:
          return USER_TABS?.ALL
        case 1:
          return USER_TABS?.ACTIVE
        case 2:
          return USER_TABS?.INACTIVE
        default:
          return USER_TABS?.ALL
      }
    }

    const response = await getUsers(
      getTab(value),
      page,
      rowsPerPage,
      searchTerm
    )
    if (response?.success) {
      setTabs([
        {
          label: 'All',
          count: response?.data?.activeUsers + response?.data?.inactiveUsers,
          color: '',
        },
        {
          label: 'Active',
          count: response?.data?.activeUsers,
          color: Colors.TEXT_COLOR_TAB_ACTIVE,
        },
        {
          label: 'Inactive',
          count: response?.data?.inactiveUsers,
          color: Colors.TEXT_COLOR_TAB_INACTIVE,
        },
      ])
      setUsers(response?.data?.data)
      setPage(response?.data?.page)
      // setRowsPerPage(response?.data?.size)
      setTotalCount(response?.data?.totalElements)
      setIsLoading(false)
    } else {
      setUsers([])
      setPage(0)
      setRowsPerPage(10)
      setTotalCount(0)
      setIsLoading(false)
    }
    // eslint-disable-next-line
  }, [page, rowsPerPage, value, searchTerm])

  const getAllUserRoles = useCallback(async () => {
    const response = await getAllRoles()
    console.log(response)
    if (response?.success) {
      setRoles(response?.data)
    }
  }, [])

  const handleSearchChange = (e: any) => {
    const value = e.target.value

    // Clear the previous timeout if it's still running
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      console.log('Debounced search term:', value)
      setSearchTerm(value)
    }, 1000)
  }

  useEffect(() => {
    getAllUserRoles()
  }, [getAllUserRoles])

  useEffect(() => {
    getAllUsersApi()
  }, [getAllUsersApi])

  return (
    <Grid
      container
      direction={'column'}
      spacing={1.5}
      height={'100%'}
      width={'100%'}
    >
      {openDialog && (
        <UserForm
          roles={roles}
          mode={dialogMode}
          openDialog={openDialog}
          handleCloseDialog={handleCloseDialog}
          data={selectedUser}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      )}
      <Grid size={{ xs: 12 }} position={'sticky'} top={0} zIndex={1} pr={2}>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          alignItems={'center'}
          flexWrap={'wrap'}
        >
          <Tabs
            TabIndicatorProps={{
              style: { display: 'none' },
            }}
            value={value}
            onChange={handleChange}
            variant='scrollable'
            scrollButtons='auto'
            aria-label='scrollable auto tabs example'
          >
            {tabs?.map((tab, index) => (
              <Tab
                key={index}
                label={
                  <Box display={'flex'} alignItems={'center'} gap={0.6}>
                    <Box component={'span'} color={tab?.color}>
                      {tab?.label}
                    </Box>
                    <span className={classes?.circleDot}></span>
                    <span>{tab?.count}</span>
                  </Box>
                }
                className={
                  value === index
                    ? classes?.tabSelected
                    : classes?.tabUnSelected
                }
              />
            ))}
          </Tabs>
          <Box flex={1} display='flex' justifyContent='flex-end' pr={3}>
            <TextField
              placeholder='Search User'
              type='search'
              size='small'
              onChange={handleSearchChange}
              slotProps={{
                input: {
                  className: classes?.searchField,
                  startAdornment: (
                    <InputAdornment position='start'>
                      <IconButton size='small'>
                        <SearchIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
          <Button
            variant='contained'
            className={classes?.createBtn}
            startIcon={<AddIcon />}
            onClick={() => handleCreate(USER_DIALOG_MODES?.CREATE)}
          >
            Create New
          </Button>
        </Box>
      </Grid>

      <Grid
        size={{ xs: 12 }}
        pr={2}
        direction={'column'}
        className={classes?.tabContentContainer}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <Grid
              container
              spacing={1.5}
              direction={'column'}
              size={{ xs: 12 }}
            >
              {users?.map((user, index) => (
                <UserInfoCard
                  key={index}
                  user={user}
                  handleDialogOpen={handleDialogOpen}
                  setSelectedUser={setSelectedUser}
                />
              ))}
            </Grid>
          </>
        )}
      </Grid>

      <Grid size={{ xs: 12 }}>
        <TablePagination
          className={classes?.cardPagination}
          component='div'
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Grid>
    </Grid>
  )
}

export default Users
